import { useQuery } from '@tanstack/react-query';
import { makeAutoObservable } from 'mobx';
import { Media, createMedia, getMediaSmallest } from '../Media/Media';
import { useBackend } from '../../Hooks/useBackend';
import { HttpBackend } from '../../Services/Http/HttpBackend';
import { BaseTrackingKey, TrackingKeysList } from '../ProgramPortfolio/TrackingKeys';
import { LocalizedEntity, createLocalizedEntity } from '../LocalizedEntity';
import { Pageable } from '../Interfaces/Pageable';

export type ActivityRequest = Pageable & {
  query?: string;
  tags?: string[];
  trackingKeys?: string[];
};

// Observable model type
export type Activity = LocalizedEntity & {
  identifier: string;
  tags: string[];
  medias: Media[];
  trackingKeys: BaseTrackingKey[];
  readonly firstSmallestMediaUrl: string | undefined;
  readonly image: { uri: string } | undefined;
  readonly media: Media | undefined;
  readonly trackingKeysSorted: BaseTrackingKey[];
  readonly durationInMinutes: number;
};

// Factory function
export function createActivity(json?: Partial<Activity>): Activity {
  const base = createLocalizedEntity(json);

  return makeAutoObservable({
    ...base,
    id: json?.id || '',
    identifier: json?.identifier || '',
    tags: json?.tags || [],
    trackingKeys: json?.trackingKeys ?? [],
    medias: (json?.medias ?? []).map((m) => createMedia(m)),

    get firstSmallestMediaUrl(): string | undefined {
      if (this.medias && this.medias.length > 0) {
        return getMediaSmallest(this.medias[0]);
      }
      return undefined;
    },

    get image(): { uri: string } | undefined {
      return this.firstSmallestMediaUrl ? { uri: this.firstSmallestMediaUrl } : undefined;
    },

    get media(): Media | undefined {
      if (this.medias && this.medias.length > 0) {
        return this.medias[0];
      }
      return undefined;
    },

    get trackingKeysSorted(): BaseTrackingKey[] {
      return this.trackingKeys.slice().sort((a, b) => {
        return TrackingKeysList.indexOf(a) - TrackingKeysList.indexOf(b);
      });
    },

    get durationInMinutes(): number {
      return 0;
    },
  });
}

// Hooks
export function useActivities(params?: Partial<ActivityRequest>) {
  const backend = useBackend();
  return useQuery({
    queryKey: ['activities', params],
    queryFn: () => backend.get('/activity/activities', params).then((res) => (res ?? []).map(createActivity)),
    staleTime: 5 * 60 * 1000,
  });
}

export function useActivity(id: string | undefined) {
  const backend = useBackend();
  return useQuery({
    queryKey: ['activity', id],
    queryFn: () => backend.get(`/activity/activities/${id}`).then((res) => (res ? createActivity(res) : undefined)),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
}

// Legacy static API - prefer hooks above
export const ActivityApi = {
  find(params?: Partial<ActivityRequest>, signal?: AbortSignal): Promise<Activity[]> {
    return HttpBackend.get('/activity/activities', params, signal ? { signal } : undefined).then((result) =>
      result.map((e: any) => createActivity(e)),
    );
  },

  get(id: string): Promise<Activity | undefined> {
    return HttpBackend.get(`/activity/activities/${id}`).then((result) =>
      result ? createActivity(result) : undefined,
    );
  },
};
